/* eslint-disable sort-imports */
import React, { useCallback, useEffect, useState } from 'react';
import Chevron from 'src/components/dashboard/components/navigationMenu/components/chevron';
import DialogModal from 'src/components/ui/dialogModal';
import Dropdown from 'src/components/ui/dropdownMenuTable/dropdownMenuTable';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import Spinner from 'src/components/ui/spinner';
import {
    DistributionRuleService,
    IDistributionRule,
} from 'src/services/distribution-rule.service';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import { formatDate } from 'src/utils/formatDate';
import { useLocalization } from 'src/utils/useLocalization';
import { useLocation, useNavigate } from 'react-router-dom';
import TableFooter from 'src/components/table/pagination/tableFooter';
import OptionsIcon from 'src/components/dealer-mgmt/options-icon';
import { useDistributionRulePermissions } from 'src/customHooks/useDistributionRulePermissions';
import { useRulePermissionDialog } from 'src/components/rule-permission-dialog';
import './styles.scss';

type SortColumn =
    | 'ruleName'
    | 'brand'
    | 'country'
    | 'createdBy'
    | 'dateCreated'
    | 'dateModified';
type SortDirection = 'asc' | 'desc';

const SYSTEM_ADMIN_TAB_KEY = 'systemAdminActiveTab';
const CONTENT_TAB_VALUE = 'Content';
const SORT_LESS = -1;
const SORT_EQUAL = 0;
const SORT_GREATER = 1;
const EMPTY_LENGTH = 0;
const CHEVRON_WIDTH = 10;
const CHEVRON_FILL = '#363b49';

const BRAND_LABELS: Record<string, string> = {
    Any: 'Any',
    VWAndAudi: 'VW and Audi',
    VW: 'VW',
    Audi: 'Audi',
};

export const getCountryLabel = (country: string): string => {
    const countryLabels: Record<string, string> = {
        US: 'US',
        usa: 'US',
        Canada: 'Canada',
        CA: 'Canada',
    };

    return countryLabels[country] ?? '';
};

export const sortRules = (
    items: IDistributionRule[],
    column: SortColumn,
    direction: SortDirection,
): IDistributionRule[] => {
    return [...items].sort((a, b) => {
        const aVal = a[column] ?? '';
        const bVal = b[column] ?? '';
        const cmp =
            aVal < bVal ? SORT_LESS : aVal > bVal ? SORT_GREATER : SORT_EQUAL;
        return direction === 'asc' ? cmp : -cmp;
    });
};

const ViewDistributionRule: React.FC = () => {
    const { systemAdmin } = useLocalization();
    const navigate = useNavigate();
    const location = useLocation();
    const { canEditRules, canDeleteRules } = useDistributionRulePermissions();
    const { showNoPermissionDialog, noPermissionDialog } =
        useRulePermissionDialog();
    const brand = 'vw6';

    const [rules, setRules] = useState<IDistributionRule[]>([]);
    const [filteredRules, setFilteredRules] = useState<IDistributionRule[]>([]);
    const [displayedRules, setDisplayedRules] = useState<IDistributionRule[]>(
        [],
    );
    const [searchInput, setSearchInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [sortColumn, setSortColumn] = useState<SortColumn>('dateCreated');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [activeEllipsisId, setActiveEllipsisId] = useState<string | null>(
        null,
    );
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [isSuccessVisible, setIsSuccessVisible] = useState(false);
    const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
    const [pickerContext] = useState(() => {
        const state = location.state as {
            selectMode?: boolean;
            returnTo?: string;
            formState?: unknown;
            showSelectBanner?: boolean;
        } | null;

        return {
            isSelectMode: Boolean(state?.selectMode),
            // Show only for upload-bulletin and Critical Organization Updates) hide it.
            showSelectBanner: state?.showSelectBanner !== false,
            returnTo:
                state?.returnTo ?? '/dashboard/systemadmin/upload-bulletin',
            formState: state?.formState,
        };
    });
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [ruleToDelete, setRuleToDelete] = useState<IDistributionRule | null>(
        null,
    );
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const message = (location.state as { successMessage?: string } | null)
            ?.successMessage;

        if (message) {
            setSuccessMessage(message);
            setIsSuccessVisible(true);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    const applySort = useCallback(
        (
            items: IDistributionRule[],
            col: SortColumn,
            dir: SortDirection,
        ): IDistributionRule[] => sortRules(items, col, dir),
        [],
    );

    useEffect(() => {
        const service = new DistributionRuleService();
        service
            .getAllDistributionRules()
            .then((response) => {
                const data: IDistributionRule[] = response.data ?? [];
                const sorted = applySort(data, 'dateCreated', 'desc');
                setRules(data);
                setFilteredRules(sorted);
            })
            .catch(() => {
                setErrorMessage(systemAdmin.DIST_RULE_ERROR);
                setHasError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [applySort]);

    const handleSearch = () => {
        const term = searchInput.trim().toLowerCase();
        const matched = term
            ? rules.filter((r) => r.ruleName.toLowerCase().includes(term))
            : rules;
        setFilteredRules(applySort(matched, sortColumn, sortDirection));
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setFilteredRules(applySort(rules, sortColumn, sortDirection));
    };

    const handleSort = (column: SortColumn, direction: SortDirection) => {
        if (column === sortColumn) {
            const toggled: SortDirection =
                sortDirection === 'asc' ? 'desc' : 'asc';
            setSortDirection(toggled);
            setFilteredRules((prev) => applySort(prev, column, toggled));
        } else {
            setSortColumn(column);
            setSortDirection(direction);
            setFilteredRules((prev) => applySort(prev, column, direction));
        }
    };

    const handleClose = () => {
        if (pickerContext.isSelectMode) {
            navigate(pickerContext.returnTo, {
                state: { formState: pickerContext.formState },
            });
            return;
        }
        localStorage.setItem(SYSTEM_ADMIN_TAB_KEY, CONTENT_TAB_VALUE);
        // eslint-disable-next-line no-magic-numbers
        window.scrollTo(0, 0);
        navigate('/dashboard/systemadmin');
    };

    const handleCreate = () => {
        if (!canEditRules) {
            showNoPermissionDialog();
            return;
        }

        if (pickerContext.isSelectMode) {
            navigate('/dashboard/systemadmin/create-distribution-rule', {
                state: {
                    selectMode: true,
                    returnTo: pickerContext.returnTo,
                    formState: pickerContext.formState,
                },
            });
            return;
        }
        navigate('/dashboard/systemadmin/create-distribution-rule');
    };

    const handleSelect = (rule: IDistributionRule) => {
        navigate(pickerContext.returnTo, {
            state: {
                selectedRule: {
                    distributionId: rule.distributionId,
                    ruleName: rule.ruleName,
                    country: rule.country,
                },
                formState: pickerContext.formState,
            },
        });
    };

    const handleAddSelectedRule = () => {
        const rule = rules.find((r) => r.distributionId === selectedRuleId);
        if (rule) handleSelect(rule);
    };

    const handleEdit = (distributionId: string) => {
        setActiveEllipsisId(null);

        if (!canEditRules) {
            showNoPermissionDialog();
            return;
        }

        navigate('/dashboard/systemadmin/edit-distribution-rule', {
            state: {
                distributionId,
                ...(pickerContext.isSelectMode && {
                    selectMode: true,
                    returnTo: pickerContext.returnTo,
                    formState: pickerContext.formState,
                }),
            },
        });
    };

    const toggleEllipsis = (distributionId: string) => {
        setActiveEllipsisId((prev) =>
            prev === distributionId ? null : distributionId,
        );
    };

    const handleDeleteClick = (rule: IDistributionRule) => {
        setActiveEllipsisId(null);

        if (!canDeleteRules) {
            showNoPermissionDialog();
            return;
        }

        setHasError(false);
        setIsSuccessVisible(false);
        setRuleToDelete(rule);
    };

    const handleDeleteCancel = () => {
        setRuleToDelete(null);
    };

    const handleDeleteConfirm = () => {
        if (!ruleToDelete) {
            return;
        }

        const { distributionId } = ruleToDelete;
        setIsDeleting(true);

        const service = new DistributionRuleService();
        service
            .deleteDistributionRule(distributionId)
            .then((response) => {
                if (response?.status !== HttpStatusCode.SUCCESS) {
                    throw Error();
                }

                if (response.data?.deleted) {
                    setRules((prev) =>
                        prev.filter(
                            (item) => item.distributionId !== distributionId,
                        ),
                    );
                    setFilteredRules((prev) =>
                        prev.filter(
                            (item) => item.distributionId !== distributionId,
                        ),
                    );
                    setSuccessMessage(systemAdmin.DIST_RULE_DELETE_SUCCESS);
                    setIsSuccessVisible(true);
                } else {
                    setErrorMessage(systemAdmin.DIST_RULE_DELETE_IN_USE);
                    setHasError(true);
                }
            })
            .catch(() => {
                setErrorMessage(systemAdmin.DIST_RULE_DELETE_ERROR);
                setHasError(true);
            })
            .finally(() => {
                setIsDeleting(false);
                setRuleToDelete(null);
                // eslint-disable-next-line no-magic-numbers
                window.scrollTo(0, 0);
            });
    };

    const sortArrows = (column: SortColumn, label: string) => {
        const isActive = column === sortColumn;
        // arrow points toward the order clicking will produce next, not the current order
        const activeClass =
            sortDirection === 'asc' ? 'rotate-down' : 'rotate-up';
        return (
            <span className='table-header__order'>
                <button
                    className='sort-control'
                    type='button'
                    onClick={() => handleSort(column, 'asc')}
                    aria-label={
                        isActive
                            ? `Toggle ${label} sort direction`
                            : `Sort ${label} ascending`
                    }
                >
                    <Chevron
                        className={`accordion__icon ${
                            isActive ? activeClass : 'rotate-up'
                        }`}
                        width={CHEVRON_WIDTH}
                        fill={CHEVRON_FILL}
                    />
                </button>
                {!isActive && (
                    <button
                        className='sort-control'
                        type='button'
                        onClick={() => handleSort(column, 'desc')}
                        aria-label={`Sort ${label} descending`}
                    >
                        <Chevron
                            className='accordion__icon rotate-down'
                            width={CHEVRON_WIDTH}
                            fill={CHEVRON_FILL}
                        />
                    </button>
                )}
            </span>
        );
    };

    const sortableHeader = (
        label: string,
        column: SortColumn,
        colClass: string,
    ) => (
        <div className={`table-header ${colClass}`}>
            <span className='u-text-truncate'>{label}</span>
            {sortArrows(column, label)}
        </div>
    );

    return (
        <>
            <div className='dist-rule-page'>
                <div className='dist-rule-wrapper'>
                    {isSuccessVisible && successMessage && (
                        <Notification
                            content={successMessage}
                            type={NotificationType.SUCCESS}
                            visibility={isSuccessVisible}
                            setVisibility={setIsSuccessVisible}
                        />
                    )}

                    {hasError && (
                        <Notification
                            content={errorMessage}
                            type={NotificationType.ERROR}
                            visibility={hasError}
                            setVisibility={setHasError}
                        />
                    )}

                    {pickerContext.isSelectMode &&
                        pickerContext.showSelectBanner && (
                            <div
                                className='dist-rule-select-banner'
                                data-testid='select-mode-banner'
                            >
                                {systemAdmin.DIST_RULE_SELECT_MODE_BANNER}
                            </div>
                        )}

                    <div className='dist-rule-wrapper__header'>
                        <div>
                            <div className='page-title'>
                                <h5>{systemAdmin.DIST_RULE_PAGE_TITLE}</h5>
                            </div>
                            <p className='dist-rule-wrapper__description'>
                                {systemAdmin.DIST_RULE_PAGE_DESCRIPTION}
                            </p>
                        </div>
                        {pickerContext.isSelectMode && (
                            <button
                                className='c-btn c-btn--secondary dist-rule-create-btn'
                                type='button'
                                onClick={handleCreate}
                                data-testid='create-rule-btn'
                            >
                                {systemAdmin.DIST_RULE_CREATE_BTN}
                            </button>
                        )}
                    </div>

                    <div className='dist-rule-search-container'>
                        <div className='search-row'>
                            <div className='search-div'>
                                <div className='c-input search-text'>
                                    <span>
                                        <i
                                            className='c-icon c-icon--[semantic-search]'
                                            aria-hidden='true'
                                            role='img'
                                        />
                                    </span>
                                    <input
                                        id='dist-rule-search-input'
                                        className='c-input__input'
                                        type='text'
                                        placeholder={
                                            systemAdmin.DIST_RULE_SEARCH_PLACEHOLDER
                                        }
                                        value={searchInput}
                                        onChange={(e) =>
                                            setSearchInput(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter')
                                                handleSearch();
                                        }}
                                        data-testid='search-input'
                                    />
                                    {searchInput && (
                                        <span
                                            id='clearSearch'
                                            className='c-input__addon'
                                            onClick={handleClearSearch}
                                            style={{ borderColor: '#d1dcde' }}
                                            data-testid='clearSearch'
                                            role='clearSearch'
                                        >
                                            <i
                                                className='c-icon c-icon--[semantic-close]'
                                                aria-hidden='true'
                                                role='img'
                                            />
                                        </span>
                                    )}
                                </div>
                                <button
                                    className='c-btn c-btn--primary search-btn'
                                    type='button'
                                    onClick={handleSearch}
                                    data-testid='search-btn'
                                >
                                    {systemAdmin.DIST_RULE_SEARCH_BTN}
                                </button>
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className='dist-rule-wrapper__spinner'>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            <div className='dist-rule-content-container'>
                                <div className='dist-rule-table'>
                                    <div className='row header-row hidden-in-list'>
                                        {sortableHeader(
                                            systemAdmin.DIST_RULE_COL_RULE_NAME,
                                            'ruleName',
                                            'col-1',
                                        )}
                                        {sortableHeader(
                                            systemAdmin.SYSTEM_ADMIN_PAGE_BRAND_LABEL,
                                            'brand',
                                            'col-2',
                                        )}
                                        {sortableHeader(
                                            systemAdmin.SYSTEM_ADMIN_PAGE_COUNTRY_LABEL,
                                            'country',
                                            'col-3',
                                        )}
                                        {sortableHeader(
                                            systemAdmin.DIST_RULE_COL_CREATED_BY,
                                            'createdBy',
                                            'col-4',
                                        )}
                                        {sortableHeader(
                                            systemAdmin.DIST_RULE_COL_CREATED_DATE,
                                            'dateCreated',
                                            'col-5',
                                        )}
                                        {sortableHeader(
                                            systemAdmin.DIST_RULE_COL_MODIFIED_DATE,
                                            'dateModified',
                                            'col-6',
                                        )}
                                        <div className='table-header col-7' />
                                    </div>

                                    {filteredRules.length === EMPTY_LENGTH ? (
                                        <div
                                            className='no-results'
                                            data-testid='no-results'
                                        >
                                            {systemAdmin.DIST_RULE_NO_RESULTS}
                                        </div>
                                    ) : (
                                        displayedRules.map((rule) => (
                                            <div
                                                key={rule.distributionId}
                                                className={`row${
                                                    pickerContext.isSelectMode
                                                        ? ` row--selectable${
                                                              selectedRuleId ===
                                                              rule.distributionId
                                                                  ? ' row--selected'
                                                                  : ''
                                                          }`
                                                        : ''
                                                }`}
                                                data-testid='rule-row'
                                                onClick={
                                                    pickerContext.isSelectMode
                                                        ? () =>
                                                              setSelectedRuleId(
                                                                  rule.distributionId,
                                                              )
                                                        : undefined
                                                }
                                            >
                                                <div className='table-row col-1'>
                                                    <span className='list-label'>
                                                        {
                                                            systemAdmin.DIST_RULE_COL_RULE_NAME
                                                        }
                                                    </span>
                                                    <button
                                                        className='c-btn c-btn--link underline'
                                                        type='button'
                                                        data-testid='rule-name-link'
                                                        onClick={() => {
                                                            if (
                                                                pickerContext.isSelectMode
                                                            )
                                                                return;
                                                            handleEdit(
                                                                rule.distributionId,
                                                            );
                                                        }}
                                                    >
                                                        {rule.ruleName}
                                                    </button>
                                                </div>
                                                <div className='table-row col-2'>
                                                    <span className='list-label'>
                                                        {
                                                            systemAdmin.SYSTEM_ADMIN_PAGE_BRAND_LABEL
                                                        }
                                                    </span>
                                                    <span>
                                                        {BRAND_LABELS[
                                                            rule.brand
                                                        ] ?? rule.brand}
                                                    </span>
                                                </div>
                                                <div className='table-row col-3'>
                                                    <span className='list-label'>
                                                        {
                                                            systemAdmin.SYSTEM_ADMIN_PAGE_COUNTRY_LABEL
                                                        }
                                                    </span>
                                                    <span>
                                                        {getCountryLabel(
                                                            rule.country,
                                                        )}
                                                    </span>
                                                </div>
                                                <div className='table-row col-4'>
                                                    <span className='list-label'>
                                                        {
                                                            systemAdmin.DIST_RULE_COL_CREATED_BY
                                                        }
                                                    </span>
                                                    <span>
                                                        {rule.createdBy}
                                                    </span>
                                                </div>
                                                <div className='table-row col-5'>
                                                    <span className='list-label'>
                                                        {
                                                            systemAdmin.DIST_RULE_COL_CREATED_DATE
                                                        }
                                                    </span>
                                                    <span>
                                                        {formatDate(
                                                            rule.dateCreated,
                                                        )}
                                                    </span>
                                                </div>
                                                <div className='table-row col-6'>
                                                    <span className='list-label'>
                                                        {
                                                            systemAdmin.DIST_RULE_COL_MODIFIED_DATE
                                                        }
                                                    </span>
                                                    <span>
                                                        {formatDate(
                                                            rule.dateModified,
                                                        )}
                                                    </span>
                                                </div>
                                                <div className='table-row col-7 options-cell'>
                                                    <span
                                                        className='options-icon'
                                                        data-testid='ellipsis-btn'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleEllipsis(
                                                                rule.distributionId,
                                                            );
                                                        }}
                                                    >
                                                        <OptionsIcon />
                                                    </span>
                                                    <Dropdown
                                                        dropdown={
                                                            activeEllipsisId ===
                                                            rule.distributionId
                                                        }
                                                        showSelected={false}
                                                        showSelectedIcon={false}
                                                        handlerChange={(
                                                            value: boolean,
                                                        ) => {
                                                            if (!value)
                                                                setActiveEllipsisId(
                                                                    null,
                                                                );
                                                        }}
                                                    >
                                                        <li
                                                            className='menu-items'
                                                            data-testid='menu-view-edit'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(
                                                                    rule.distributionId,
                                                                );
                                                            }}
                                                        >
                                                            <span>
                                                                {
                                                                    systemAdmin.DIST_RULE_MENU_VIEW_EDIT
                                                                }
                                                            </span>
                                                        </li>
                                                        <li
                                                            className='menu-items'
                                                            data-testid='menu-delete'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteClick(
                                                                    rule,
                                                                );
                                                            }}
                                                        >
                                                            <span className='delete'>
                                                                {
                                                                    systemAdmin.DIST_RULE_MENU_DELETE
                                                                }
                                                            </span>
                                                        </li>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <TableFooter
                                    brand={brand}
                                    content={filteredRules}
                                    setDisplayedContent={setDisplayedRules}
                                    defaultPerPage={10}
                                    perPageOptions={['10', '25']}
                                />
                            </div>

                            <div
                                className={`dist-rule-wrapper__footer ${
                                    pickerContext.isSelectMode
                                        ? 'dist-rule-wrapper__footer--select'
                                        : ''
                                }`}
                            >
                                {pickerContext.isSelectMode ? (
                                    <>
                                        <button
                                            className='c-btn c-btn--secondary'
                                            type='button'
                                            onClick={handleClose}
                                            data-testid='select-cancel-btn'
                                        >
                                            {
                                                systemAdmin.DIST_RULE_SELECT_CANCEL_BTN
                                            }
                                        </button>
                                        <button
                                            className='c-btn'
                                            type='button'
                                            disabled={!selectedRuleId}
                                            onClick={handleAddSelectedRule}
                                            data-testid='add-selected-rule-btn'
                                        >
                                            {
                                                systemAdmin.DIST_RULE_ADD_SELECTED_RULE_BTN
                                            }
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className='c-btn c-btn--secondary'
                                        type='button'
                                        onClick={handleClose}
                                        data-testid='close-btn'
                                    >
                                        {systemAdmin.DIST_RULE_CLOSE_BTN}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div className='dist-rule-privacy-footer'>
                    <FooterPrivacy />
                </div>
            </div>
            <DialogModal
                titleContent={systemAdmin.DIST_RULE_DELETE_MODAL_TITLE}
                bodyContent={systemAdmin.DIST_RULE_DELETE_MODAL_BODY}
                open={!!ruleToDelete}
                cancelBtnLabel={systemAdmin.DIST_RULE_DELETE_MODAL_CANCEL}
                confirmBtnLabel={systemAdmin.DIST_RULE_DELETE_MODAL_CONFIRM}
                onCancelHandler={handleDeleteCancel}
                onConfirmHandler={handleDeleteConfirm}
                isLoading={isDeleting}
                isConfirmBtnRed
                keepOpenOnConfirm
            />
            {noPermissionDialog}
        </>
    );
};

export default ViewDistributionRule;
